import { useQuery } from '@apollo/client/react';
import {
  normalizeSlices,
  SliceContainer,
  defaultSlicesMapper,
  TextAds,
} from '@scorenco/components';
import { ssrApolloService } from '@scorenco/core';
import {
  NextAdArea,
  NextAkceloConfig,
  NextGooglePublisherTagConfig,
  NextOptiDigitalConfig,
  NextPageWithLayout,
} from '@scorenco/next';
import Error from 'next/error';

import { DebugMode, DebugModeStrapiLink, StrapiPageSeo } from '../components';
import { WebLayout } from '../layouts';
import { RouteKey } from '../routes';

import { GetHomePageDocument } from './_internal/Home.query.generated';
import { HomeHero } from './_internal/HomeHero';

const Home: NextPageWithLayout<{ preview?: boolean }> = ({ preview }) => {
  const homePageQuery = useQuery(GetHomePageDocument, {
    context: {
      clientName: 'strapi',
    },
  });

  const homePage = homePageQuery.data?.pagesHome;

  if (!homePage && !homePageQuery.loading) {
    return <Error statusCode={404} />;
  }

  const title = homePage?.title;
  const subtitle = homePage?.subtitle as string;
  const images = homePage?.images;
  const ctaLabel = homePage?.ctaLabel as string;
  const seo = homePage?.seo;
  const slices = normalizeSlices(homePage?.body);

  return (
    <div>
      {seo && <StrapiPageSeo seo={seo} url={{ routeKey: RouteKey.Home }} />}
      <NextOptiDigitalConfig adUnit="/157593316/od.scorenco.com/home" />
      <NextAkceloConfig />
      <NextGooglePublisherTagConfig />

      {title && (
        <HomeHero
          title={title}
          images={images}
          subtitle={subtitle}
          ctaLabel={ctaLabel}
        />
      )}

      <div style={{ backgroundColor: 'white', padding: '1rem 0' }}>
        <div style={{ margin: '0 auto', width: '50%' }}>
          <TextAds
            display_type="homepage"
            sport_id={undefined}
            department_id={undefined}
            logged_in={0}
          />
        </div>
      </div>

      <NextAdArea
        akceloSlotId="Scorenco_PlayRVideo"
        akceloAdUnitClass="akcelo-adunit-15786"
        withGooglePublisherTag
      />

      <SliceContainer slices={slices} mapper={defaultSlicesMapper} />

      <DebugMode preview={preview}>
        <DebugModeStrapiLink type="pages-home" />
      </DebugMode>
    </div>
  );
};

export const getServerSideProps = WebLayout.buildGetServerSideProps({}, [
  async (options, acc: { props: any }) => {
    options.res.setHeader(
      'Cache-Control',
      `public, s-maxage=${30 * 24 * 60 * 60}, max-age=${15 * 60}`
    );

    const { apolloClient, addApolloState } = ssrApolloService();

    if (options.query['no-ssr'] === undefined) {
      const getHomeQuery = await apolloClient.query({
        query: GetHomePageDocument,
        context: {
          clientName: 'strapi',
        },
      });

      if (!getHomeQuery.data) {
        return { notFound: true };
      }
    }

    return addApolloState({
      ...acc,
      props: { ...acc.props, preview: options?.preview ? true : null },
    });
  },
]);

Home.getLayout = WebLayout.buildGetLayout({ strapiMenu: true });

export default Home;
